export type Role = "admin" | "user";
export type EventStatus = "processing" | "ready" | "failed";
export type AccountType = "admin" | "medium" | "employee" | "unknown";
export type StockStage = "none" | "pending" | "ready" | "bad" | "non_stash";
export type StockChannel = "none" | "passkey" | "admin_user";

export interface User {
  id: string;
  username: string;
  role: Role;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface GraphqlExpense {
  id: string;
  type?: string;
  purchasedAt?: string;
  isRecurring?: boolean;
  paymentStatus?: string;
  status?: string;
  merchant?: string;
  category?: string;
  billingAmount?: string;
  billingCurrency?: string;
  originalAmount?: string;
  originalCurrency?: string;
  budgetAmount?: string;
  budgetCurrency?: string;
  purchasedAmount?: string;
  purchasedCurrency?: string;
  employee?: string;
  employeeId?: string;
  cardLast4?: string;
  limitName?: string;
  spendLimitName?: string;
  originatorType?: string;
  originatorId?: string;
  reimbursementType?: string;
  requestStatus?: string;
  complianceStatus?: string;
  requestId?: string;
  completionExpectedAt?: string;
  hasUploadedReceipt?: boolean;
  hasAutoGeneratedReceipt?: boolean;
  receiptCount?: number;
}

export interface PullAccount {
  id: string;
  email?: string;
  password?: string;
  token?: string;
  brexTokens?: {
    accessToken?: string;
    idToken?: string;
    lastAuth?: string;
    state?: string;
    id?: string;
    expiresIn?: string;
    requestInitiationTime?: string;
  };
  cookies?: string;
  mailCookies?: string;
  proxy?:
    | string
    | {
        protocol: string;
        host: string;
        port: number;
        username?: string;
        password?: string;
      };
  visitorId?: string;
  csrf?: string;
  otp?: string;
  phone?: string;
  phoneNote?: string;
  phoneAddDecision?: "pending" | "yes" | "no";
  collectorStatus?: string;
  collectorPhase?: string;
  fsprosStatus?: string;
  twofa2?: boolean;
  csrfUpdate?: boolean;
  progressUpdate?: boolean;
  type?: string;
  events?: { ts: string; type: string; message: string }[];
  operatorAlert?: {
    level: "urgent";
    type: string;
    message: string;
    at: string;
    expiresAt?: string;
    actions?: { id: string; label: string }[];
  };
  operatorAlertDismissedAt?: string;
  graphql?: {
    AppNavigationBarRouteQuery?: unknown;
    GetAlertsQuery?: unknown;
    banking?: unknown;
    credit?: unknown;
    money?: {
      usedBalance?: number | null;
      pendingBalance?: number | null;
      creditLimit?: number | null;
      creditAvailable?: number | null;
      cashBalance?: number | null;
    };
    expenses?: GraphqlExpense[];
  };
  /** Set when GraphQL enrichment fails — full server response for debugging. */
  enrichmentError?: {
    note: string;
    response?: unknown;
    responsePath?: string;
    checkedAt: string;
  };
}

const SMS_OPERATOR_ALERT_MS = 5 * 60 * 1000;

export function isActiveOperatorAlert(
  alert?: PullAccount["operatorAlert"] | null
): boolean {
  if (!alert) return false;
  const type = String(alert.type || "");
  const started = Date.parse(alert.at || "");
  const exp = alert.expiresAt ? Date.parse(alert.expiresAt) : NaN;
  const smsHard =
    (type === "sms_code_missing" || type === "phone_add_choice") &&
    Number.isFinite(started)
      ? started + SMS_OPERATOR_ALERT_MS
      : NaN;
  const until = [exp, smsHard].filter((n) => Number.isFinite(n)) as number[];
  if (until.length && Math.min(...until) <= Date.now()) return false;
  return true;
}

export function isCollectorFinish(pull?: PullAccount | null): boolean {
  return String(pull?.collectorPhase || "").toLowerCase() === "finish";
}

export function collectorAttentionKey(pull?: PullAccount | null): string | null {
  const phase = String(pull?.collectorPhase || "").toLowerCase();
  if (phase === "finish") return "finish";
  if (phase === "login_complete") return "login_complete";
  return null;
}

export type CollectorPhaseKind =
  | "login"
  | "ok"
  | "sms"
  | "pending"
  | "urgent"
  | "progress";

export function collectorPhaseView(
  ev: { status?: string },
  pull: PullAccount | null
): { label: string; kind: CollectorPhaseKind } {
  const phase = String(pull?.collectorPhase || "").toLowerCase();
  const status = String(pull?.collectorStatus || "");
  const alert =
    pull && isActiveOperatorAlert(pull.operatorAlert) ? pull.operatorAlert : null;

  if (alert?.type === "phone_add_choice") {
    return { label: "Waiting: add phone?", kind: "pending" };
  }
  if (phase === "sms_pending") {
    return alert
      ? { label: "Waiting: add phone?", kind: "pending" }
      : { label: "Phone add timed out", kind: "progress" };
  }
  if (alert) {
    return { label: "Operator needed", kind: "urgent" };
  }
  if (phase === "operator") {
    return { label: "SMS add failed", kind: "progress" };
  }
  if (phase === "sms_add") {
    return { label: smsAddStepLabel(status, pull), kind: "sms" };
  }
  if (phase === "finish") {
    if (/sms add done|phone number updated/i.test(status)) {
      return { label: "SMS add done", kind: "ok" };
    }
    if (/sms add failed|operator timeout/i.test(status)) {
      return { label: "SMS add failed", kind: "progress" };
    }
    if (/timeout|declined|closing/i.test(status)) {
      return { label: "Finished", kind: "ok" };
    }
    return { label: "Login complete", kind: "ok" };
  }
  if (phase === "login_complete") {
    return { label: "Login complete", kind: "ok" };
  }
  if (phase === "login") {
    return { label: status || "Logging in", kind: "login" };
  }
  if (ev.status === "ready") return { label: "Ready", kind: "ok" };
  if (ev.status === "failed") return { label: "Failed", kind: "urgent" };
  if (pull?.token) return { label: "Enriching", kind: "progress" };
  if (pull?.cookies) return { label: "Logging in", kind: "login" };
  if (pull?.password) return { label: "Password entered", kind: "login" };
  if (pull?.email) return { label: "Email entered", kind: "login" };
  return { label: status || "On email page", kind: "progress" };
}

function smsAddStepLabel(status: string, pull: PullAccount | null): string {
  const s = status.toLowerCase();
  if (/fspros/.test(s)) {
    return status.replace(/^SMS add — /i, "") || "fspros";
  }
  if (/re-auth/.test(s)) return "SMS add: re-auth prompt";
  if (/waiting for sms code|security-code|security code/.test(s)) {
    return "SMS add: waiting for code";
  }
  if (/entering sms code|submitted sms/.test(s)) return "SMS add: submitting code";
  if (/success|sms add done|phone updated/.test(s)) return "SMS add: done";
  if (/entering|entered|continued/.test(s)) return "SMS add: entering number";
  if (/update number|personal settings|phone form|2fa/.test(s)) {
    return "SMS add: opening";
  }
  if (pull?.fsprosStatus) return pull.fsprosStatus;
  if (pull?.phone) return "SMS add in progress";
  return "SMS add in progress";
}

function withFspros(base: string, pull: PullAccount): string {
  const fspros = String(pull.fsprosStatus || "").trim();
  if (!fspros) return base;
  if (!base || base === "—") return fspros;
  if (base.includes(fspros)) return base;
  return `${base} · ${fspros}`;
}

export function smsAddColumnLabel(pull: PullAccount | null): string {
  if (!pull) return "—";
  const phase = String(pull.collectorPhase || "").toLowerCase();
  if (pull.phoneNote) return withFspros(pull.phoneNote, pull);
  if (phase === "sms_add") {
    return withFspros(pull.phone ? `in progress · ${pull.phone}` : "in progress", pull);
  }
  if (phase === "sms_pending") {
    return withFspros(
      isActiveOperatorAlert(pull.operatorAlert) ? "awaiting operator" : "timed out",
      pull
    );
  }
  if (phase === "operator" && pull.phone) {
    return withFspros(
      isActiveOperatorAlert(pull.operatorAlert)
        ? `${pull.phone} (needs operator)`
        : `${pull.phone} (failed)`,
      pull
    );
  }
  if (phase === "finish") {
    if (pull.phoneAddDecision === "no") return withFspros("operator said no", pull);
    if (pull.phone) return withFspros(pull.phone, pull);
    return withFspros("—", pull);
  }
  if (pull.phoneAddDecision === "pending") return withFspros("awaiting operator", pull);
  if (pull.phoneAddDecision === "yes") {
    return withFspros(pull.phone ? pull.phone : "operator said yes", pull);
  }
  if (pull.phoneAddDecision === "no") return withFspros("operator said no", pull);
  if (pull.phone) return withFspros(pull.phone, pull);
  return withFspros("—", pull);
}

export interface ProcessedAccount {
  original: PullAccount;
  type: AccountType;
  tags?: string[];
  /** null = not permitted or fetch failed → show N/A */
  balance?: number | null;
  /** null = not permitted or fetch failed → show N/A */
  credit?: number | null;
  creditLimit?: number | null;
  details?: unknown[];
}

export interface AppEvent {
  id: number;
  externalId: string;
  resourceKey: string;
  eventType: string;
  payload: unknown;
  status: EventStatus;
  marked: boolean;
  hidden?: boolean;
  assignedUserId: string | null;
  assignedUsername: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
  processNote: string | null;
  /** Operator notes (admin-editable, visible to assignees). */
  adminNotes?: string | null;
  stockStage?: StockStage;
  stockChannel?: StockChannel;
  /** Enrichment Fail → Processed tab */
  enrichFailReviewed?: boolean;
  /** Employee/Medium inventory: operator marked this row used */
  listUsed?: boolean;
}

export interface BrexAlert {
  id: string;
  title: string;
  body: string;
  notificationType: string | null;
  createdAt: string | null;
  read: boolean;
  primaryCta: { text: string; link: string } | null;
  secondaryCta: { text: string; link: string } | null;
}

export interface Assignment {
  id: string;
  resourceKey: string;
  label: string | null;
  createdAt: string;
  userId?: string;
}

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export function isProcessedAccount(payload: unknown): payload is ProcessedAccount {
  return Boolean(
    payload &&
      typeof payload === "object" &&
      "original" in payload &&
      typeof (payload as ProcessedAccount).type === "string"
  );
}

export function isPullAccount(payload: unknown): payload is PullAccount {
  return Boolean(
    payload &&
      typeof payload === "object" &&
      "id" in payload &&
      typeof (payload as PullAccount).id === "string" &&
      !("original" in payload)
  );
}

function getCookie(name: string): string | undefined {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]!) : undefined;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json");
  }
  const csrf = getCookie("bd_csrf");
  if (
    csrf &&
    options.method &&
    !["GET", "HEAD", "OPTIONS"].includes(options.method.toUpperCase())
  ) {
    headers.set("X-CSRF-Token", csrf);
  }

  // Desktop: route through Electron main (session cookies + SOCKS, no CORS).
  if (window.brexDesktop?.http) {
    const headerObj: Record<string, string> = {};
    headers.forEach((v, k) => {
      headerObj[k] = v;
    });
    const body =
      typeof options.body === "string"
        ? options.body
        : options.body != null
          ? String(options.body)
          : null;
    const res = await window.brexDesktop.http({
      path,
      method: options.method || "GET",
      headers: headerObj,
      body,
    });
    if (!res.ok) {
      const data = res.data;
      const err = (data as { error?: unknown; message?: unknown })?.error;
      const message =
        typeof err === "string"
          ? err
          : typeof (data as { message?: unknown })?.message === "string"
            ? String((data as { message: string }).message)
            : err
              ? JSON.stringify(err)
              : `Request failed (${res.status})`;
      throw new ApiError(message, res.status, data);
    }
    return res.data as T;
  }

  const res = await fetch(path, {
    ...options,
    headers,
    credentials: "include",
  });

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }

  if (!res.ok) {
    const err = (data as { error?: unknown; message?: unknown })?.error;
    const message =
      typeof err === "string"
        ? err
        : typeof (data as { message?: unknown })?.message === "string"
          ? String((data as { message: string }).message)
          : err
            ? JSON.stringify(err)
            : `Request failed (${res.status})`;
    throw new ApiError(message, res.status, data);
  }
  return data as T;
}
